import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import { logger } from './logger.js';
import { platformBrandsRepo } from './repositories/earnings-repo.js';
import { loadBrand, resolveBrandIdForHost } from './brand-resolver.js';
import type { PlatformBrand } from '@kilat/shared';
import { CONNECTION_GATE_TIMEOUT_MS, ConnectionGateError } from '@kilat/ui';

/**
 * Sprint 88 — `usePlatformBrand` for agent-pwa, mirror of the
 * marketing/dashboard/customer-pwa providers (ADR-0017).
 *
 * Resolves the brand once from the request hostname, then live-listens
 * to `platformBrands/{brandId}` so a super-admin rename or domain
 * rotation propagates without a reload.
 *
 * Until this hook is consumed everywhere in the agent-pwa shell, the
 * existing literal "Kilat" / "Kilat Agent" strings in `app-shell.tsx`
 * and `routes/home.tsx` still render — those replacements are a
 * follow-up task; this provider unblocks them.
 */
const BrandContext = createContext<PlatformBrand | null>(null);

export function PlatformBrandProvider({
  host,
  children,
  fallback = null,
}: {
  readonly host?: string;
  readonly children: ReactNode;
  readonly fallback?: ReactNode;
}) {
  const [brand, setBrand] = useState<PlatformBrand | null>(null);
  const [failed, setFailed] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let unsubLive: (() => void) | null = null;
    setFailed(false);
    // Nothing renders until this read lands, and an offline Firestore read
    // waits rather than failing — so give it a deadline, then say so.
    const deadline = window.setTimeout(() => {
      if (!cancelled) setFailed(true);
    }, CONNECTION_GATE_TIMEOUT_MS);

    (async () => {
      try {
        const resolvedHost = host ?? window.location.hostname;
        const brandId = await resolveBrandIdForHost(resolvedHost);
        const initial = await loadBrand(brandId);
        if (cancelled) return;
        window.clearTimeout(deadline);
        setBrand(initial);
        unsubLive = platformBrandsRepo.snapshot(brandId, (next) => {
          if (cancelled || !next) return;
          setBrand(next);
        });
      } catch (e) {
        logger.error('PlatformBrandProvider failed', { error: String(e) });
        // Used to stop here: the app sat on its splash forever.
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(deadline);
      if (unsubLive) unsubLive();
    };
  }, [host, retryTick]);

  if (!brand && failed) {
    return <ConnectionGateError onRetry={() => setRetryTick((n) => n + 1)} />;
  }
  if (!brand) return <>{fallback}</>;
  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
}

export function usePlatformBrand(): PlatformBrand {
  const v = useContext(BrandContext);
  if (!v) throw new Error('usePlatformBrand must be used inside <PlatformBrandProvider>');
  return v;
}
